/**
 * The Stripe mode guard's logic, shared by consumer (pay.parkify.com checkout +
 * add-card) and enforcement-portal (paying a notice). Each app keeps its own
 * markup and its own `@stripe/stripe-js` import; this module has no Stripe
 * dependency at all.
 *
 * Why the guard exists: the card form fails SILENTLY when the site's pk_* and
 * the Worker's sk_* are in different modes (test vs live). Elements never
 * mounts, the Pay button stays disabled, and nothing reaches the console. The
 * guard asks the Worker for its mode at mount and lets the app render a clear
 * message in place of <Elements/> when they diverge.
 *
 * Fails OPEN on a network error or timeout (`match`): availability on a
 * real-money page beats catching a rare misconfiguration mid-incident. Because
 * a fail-open guard is invisible when it degrades, enforcement-portal's
 * __tests__/StripeModeGuard.test.tsx is its liveness probe; keep it passing.
 *
 * The technical detail (which side is in which mode) goes to the console for
 * operators. A parker-facing screen never names the vendor or the modes.
 */
import { useEffect, useState } from 'react'

export type StripeMode = 'test' | 'live' | 'unknown'

/** The mode a publishable key is in. Anything that is not pk_live_/pk_test_ is unknown. */
export function stripeModeFromKey(key: string | null | undefined): StripeMode {
  if (key?.startsWith('pk_live_')) return 'live'
  if (key?.startsWith('pk_test_')) return 'test'
  return 'unknown'
}

export interface StripeModeCheck {
  status: 'pending' | 'match' | 'mismatch' | 'unknown'
  client: StripeMode
  server: StripeMode | null
}

/** How long the server read may take before the guard fails open. */
export const STRIPE_MODE_TIMEOUT_MS = 3000

/**
 * Compare this build's key mode with the Worker's, once per mount.
 *
 * `unknown` (either side's mode unreadable) BLOCKS like `mismatch`: a key that
 * is neither pk_live_ nor pk_test_, or a Worker that cannot say, is not a
 * configuration anyone can take a payment on. Only a failed or timed-out read
 * fails open.
 */
export function useStripeModeCheck(
  clientStripeMode: () => StripeMode,
  fetchStripeMode: (signal: AbortSignal) => Promise<StripeMode>,
): StripeModeCheck {
  const [result, setResult] = useState<StripeModeCheck>(() => ({
    status: 'pending',
    client: clientStripeMode(),
    server: null,
  }))

  useEffect(() => {
    let cancelled = false
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), STRIPE_MODE_TIMEOUT_MS)
    const client = clientStripeMode()

    fetchStripeMode(ctrl.signal)
      .then((server) => {
        if (cancelled) return
        const status = server === 'unknown' || client === 'unknown' ? 'unknown'
          : client === server ? 'match' : 'mismatch'
        if (status !== 'match') {
          // Operators get the actionable detail; the screen gets calm copy.
          console.error('stripe-mode mismatch — align the payment keys', { client, server })
        }
        setResult({ status, client, server })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        console.warn('stripe-mode check failed; failing open to the card form', {
          error: e instanceof Error ? e.message : String(e),
        })
        setResult({ status: 'match', client, server: null })
      })
      .finally(() => clearTimeout(timer))

    return () => {
      cancelled = true
      clearTimeout(timer)
      ctrl.abort()
    }
    // Once per mount, like both guards before it: the key is build-time and the
    // server's mode does not change under a page.
  }, [])

  return result
}
